import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bProductsInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-products-info'
import { useFpCategoryPromotionStore } from '@cart-services/hooks/useFpCategoryPromotionStore'
import { type MergedCoupon } from '@cart-services/types'
import { sortCoupons } from '@cart-services/utils/sortCoupons'
import { CarouselCategoryPromotion } from '@cart-ui/CategoryPromotion/CarouselCategoryPromotion'
import { CategoryPromotionAnnouncement } from '@cart-ui/CategoryPromotion/CategoryPromotionAnnouncement'
import { CategoryPromotionImages } from '@cart-ui/CategoryPromotion/CategoryPromotionImages'
import { parseCouponSpecificProductsMap } from '@cart-ui/PromotionCouponList/parseCouponSpecificProductsMap'
import { PromotionCouponList } from '@cart-ui/PromotionCouponList/PromotionCouponList'
import { useFastpassMerchantGoogleAnalyticEvent } from '@dni-analysis'
import { type PromotionPageEvent } from '@dni-analysis/types/fastpass-merchant-events'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignBanners } from '@hooks-api/fortune/useApiCampaignBanners'
import { useApiCouponsSpecificProducts } from '@hooks-api/fortune/useApiCouponsSpecificProducts'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { VStack } from '@pets-ui/VStack/VStack'
import { useMemo } from 'react'
import { type ItemWantToAdd, useHandleProductAddToCart } from '../../../../fp-b2b-shared/hooks/use-handle-product-add-to-cart'
import { useProductCatalog } from '../../../../fp-b2b-shared/hooks/use-product-catalog'

export const CategoryPromotion = () => {
  const { focusCouponId, setFocusCouponId } = useFpCategoryPromotionStore()
  const { campaignId, projectCode, countryCode, currency, campaignCode, platform } = useProductCatalog()
  const { items: cartItems } = useFpB2bCartService(campaignId, platform)
  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCode)
  const { availableMergedCoupons } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)
  const { destructuredData: couponSpecificProducts } = useApiCouponsSpecificProducts({
    campaign_code: campaignCode,
    coupon_ids: availableMergedCoupons?.map(coupon => coupon.coupon_id),
  })
  const specificProductIds = useMemo(() => [...new Set(
    couponSpecificProducts?.flatMap(product => product.product_groups.flatMap(group => group.map(item => item.product_id))),
  )], [couponSpecificProducts])
  const { productMap } = useFpB2bProductsInfo({
    projectCode,
    countryCode,
    campaignCode,
    campaignId,
    extraProductIds: specificProductIds,
    platform,
  })
  const { sendPromotionPageEvent, sendExpandCouponParentProductEvent, sendExpandCouponChildProductEvent } = useFastpassMerchantGoogleAnalyticEvent({ campaignId, projectCode, countryCode })
  const { destructuredData: campaignBanners } = useApiCampaignBanners({
    campaign_code: campaignCode,
  })
  const { handleProductAddToCart } = useHandleProductAddToCart()
  const handleCarouselChange = useCallbackEvent(() => {
    sendPromotionPageEvent('fastpass_merchant_click_main_banner')
  })

  const couponSpecificProductsMap = useMemo(() => {
    if (!couponSpecificProducts || !productMap) return new Map()
    return parseCouponSpecificProductsMap(couponSpecificProducts, productMap)
  }, [couponSpecificProducts, productMap])

  const handleFocusEnd = useCallbackEvent(() => {
    setFocusCouponId(null)
  })

  const handleQuantityChange = useCallbackEvent((product: ECommerceProduct, quantity?: number) => {
    if (!product.product_id || !product.sku) return
    const itemWantToAdd: ItemWantToAdd = {
      cartItemId: product.product_id,
      product_id: product.parent_product_id,
      variation_id: product.product_id,
      name: product.name,
      sale_price: Number(product.sale_price),
      sku: product.sku,
      project_code: product.project_code,
      quantity,
      delivery_class: product.delivery_class,
    }
    handleProductAddToCart(itemWantToAdd)
  })

  const sortedCoupons = useMemo(() => availableMergedCoupons ? sortCoupons(availableMergedCoupons) : undefined, [availableMergedCoupons])

  const handleSendAnalyticsInToggleEvent = useCallbackEvent((isToggleOpen: boolean, handler: () => void) => {
    if (!isToggleOpen) return
    handler()
  })

  const handleToggleAnnouncement = useCallbackEvent((isOpen: boolean) => {
    handleSendAnalyticsInToggleEvent(isOpen, () => sendPromotionPageEvent('fastpass_merchant_click_announcement'))
  })

  const handleImageClickRedirect = useCallbackEvent((type: PromotionPageEvent['eventName']) => () => {
    sendPromotionPageEvent(type)
  })

  const handleToggleCouponProducts = (coupon: MergedCoupon, isOpen: boolean) => {
    handleSendAnalyticsInToggleEvent(isOpen, () => sendExpandCouponParentProductEvent(coupon))
  }
  const handleToggleCouponProduct = (variations: ECommerceProduct[], isOpen: boolean) => {
    if (!isOpen) return
    handleSendAnalyticsInToggleEvent(isOpen, () => sendExpandCouponChildProductEvent(variations))
  }

  return (
    <VStack gap={3}>
      <CarouselCategoryPromotion
        campaignBanners={campaignBanners}
        onCarouselChange={handleCarouselChange}
        onClickRedirect={handleImageClickRedirect('fastpass_merchant_click_main_banner')}
      />
      <CategoryPromotionImages
        campaignBanners={campaignBanners}
        onClickRedirect={handleImageClickRedirect('fastpass_merchant_click_secondary_banner')}
      />
      <CategoryPromotionAnnouncement
        title="最新公告"
        description={exhibitorSettings?.announcement}
        onToggle={handleToggleAnnouncement}
      />
      <PromotionCouponList
        onToggleCouponProducts={handleToggleCouponProducts}
        onToggleCouponProduct={handleToggleCouponProduct}
        coupons={sortedCoupons}
        couponSpecificProductsMap={couponSpecificProductsMap}
        currency={currency}
        focusCouponId={focusCouponId}
        onFocusEnd={handleFocusEnd}
        cartItems={cartItems}
        onQuantityChange={handleQuantityChange}
        scrollMarginTop={27}
      />
    </VStack>
  )
}
